import { pricingCalculator } from 'components/common/VendorPricingCalc';
import {
  calculateMealEquivalents,
  calculateMealPricing,
  isFoodCardOrder,
} from 'components/common/food/utils/foodUtils';
import { PROGRAM_VENDOR_CONFIG, VENDORS } from 'components/common/vendorConfig';
import type { VendorDefaultPrice } from 'components/common/vendorConfig';
import type { Bundle, BundleEntry, Observation, Patient } from 'fhir/r4';
import _ from 'lodash';
import moment from 'moment';
import { useEffect, useState } from 'react';
import React from 'react';
import { GiHealthNormal } from 'react-icons/gi';
import { DatesRangeInput } from 'semantic-ui-calendar-react';
import { Loader, Message, Icon, Select, Button, Form, Confirm } from 'semantic-ui-react';
import type { DropdownProps, StrictCheckboxProps } from 'semantic-ui-react';
import useSWR from 'swr';
import type { SWRConfiguration } from 'swr';
import type {
  Fetched,
  FlatPatient,
  FoodItem,
  FoodOrder,
  FoodOrderBundle,
  FoodOrderCreateResponse,
  FoodOrderSubmitResponse,
  OrderNote,
  OrderPostPayload,
  ProgramBudget,
  ReferralSummary,
  VendorPriceRow,
  VendorPricingByCode,
} from 'types';
import {
  mapKey,
  resetProgramOrderCost,
  setProgramBudgetByReferralId,
  updateProgramBudgetByReferralId,
} from 'utils';

import { useAuthContext } from '../../../../contexts/AuthContext';
import { MultiFetcher } from '../../../../data/FHIRFetcher';
import FoodDelete from '../../../../data/FoodDelete';
import FoodFetcher, { MultiFoodFetcher } from '../../../../data/FoodFetcher';
import FoodPoster from '../../../../data/FoodPoster';
import FoodUpdater from '../../../../data/FoodUpdater';
import { apiClient } from '../../../../services/api-client';
import * as FHIRUtils from '../../../../utils/FHIRUtils';
import FoodOrderTriageModalV2 from '../foodOrderTriageModalV2';

import AddOrder from './add';
import { foodNameMap, foodTypeMap, statusColors } from './constants';
import ExportOrders from './export';
import OrdersTable from './table';
import UpdateOrder from './update';

/**
 * The sort-and-filter state behind the orders table.
 *
 * `useReducer` seeds all three as `null`, and `OrdersTableProps` in `./table.tsx`
 * mirrors that — the table renders an unsorted, empty grid until the first
 * `CLEAR_FILTERS` arrives with a dataset.
 */
type OrdersTableState = {
  // `null` from the seed, `undefined` from SWR's `data`: both really occur.
  data: FoodOrder[] | null | undefined;
  direction: 'ascending' | 'descending' | null;
  /**
   * Mirrors `OrdersTableAction['sortcolumn']`, which `CHANGE_SORT` stores verbatim —
   * including the iteratee function described in the defect note on that field.
   * `undefined` because the action field is optional.
   */
  sortcolumn: string | ((order: FoodOrder) => unknown) | null | undefined;
};

/**
 * Every action this reducer is dispatched.
 *
 * `datevalue` belongs only to `FILTER` and `sortcolumn` only to `CHANGE_SORT`,
 * but they sit on one object rather than a discriminated union, matching how the
 * reducer body reads them.
 */
type OrdersTableAction = {
  // Explicit `| undefined`: the dispatches pass the property present and holding
  // `undefined`, which a bare `?` rejects under `exactOptionalPropertyTypes`.
  data?: FoodOrder[] | null | undefined;
  datevalue?: string | null;
  /**
   * SUSPECTED DEFECT (the default order sort silently does nothing) — REPORTED, NOT
   * FIXED. Every other dispatcher passes a column NAME here, but the `CHANGE_SORT`
   * raised after the dataset loads passes a lodash iteratee FUNCTION
   * (`(od) => [statusColors[od.status], od.orderDate]`), intending to sort by status
   * colour then date.
   *
   * The reducer does not call it. It reads `o[mapKey(action.sortcolumn)]`, so the
   * function is stringified to its own source text and used as a property name; that
   * key is absent from every row, `_.sortBy` sees one constant key, and the rows come
   * back in their original order. The orders table therefore renders unsorted where
   * the code intends a status-then-date sort — silently, since an unsorted table looks
   * plausible.
   *
   * The union states what is really passed. Narrowing it to `string` would make this
   * dispatch a compile error rather than a silent no-op, but choosing between calling
   * the iteratee and dropping it changes the row order the care team sees.
   */
  sortcolumn?: string | ((order: FoodOrder) => unknown);
  type: string;
};

/**
 * SUSPECTED DEFECT — REPORTED, NOT FIXED. Three of this reducer's four data reads
 * assume a dataset that the initial state does not have.
 *
 * `useReducer` seeds `data: null`. `CHANGE_SORT` then calls `state.data.slice()`
 * and `_.sortBy(state.data, …)` — the first throws a TypeError on `null`, the
 * second silently returns `[]`, so the two arms of the same action disagree about
 * what an empty state means. `FILTER` calls `action.data.filter(…)` while `data`
 * is optional on the action type, so a `FILTER` dispatched without one throws.
 *
 * None fires today: every dispatch site in this file passes a `data` array, and
 * the sort headers that raise `CHANGE_SORT` live inside a table that is only
 * mounted once a dataset exists. `FILTER` has no dispatcher at all here. The
 * reads are asserted rather than guarded because choosing what an unsorted `null`
 * should sort to is a product decision, and because `?? []` would convert the
 * throw into a table that silently renders empty.
 */
function Reducer(state: OrdersTableState, action: OrdersTableAction): OrdersTableState {
  switch (action.type) {
    case 'CHANGE_SORT':
      if (state.sortcolumn === action.sortcolumn) {
        return {
          ...state,
          data: state.data!.slice().reverse(),
          direction: state.direction === 'ascending' ? 'descending' : 'ascending',
        };
      }
      return {
        sortcolumn: action.sortcolumn,
        data: _.sortBy(state.data, [
          function (o) {
            // `sortcolumn` is optional on the merged action type because only
            // `CHANGE_SORT` carries it — which is the case we are in. `mapKey`
            // names the coercion the subscript already performed rather than
            // claiming a narrowing the declaration does not support.
            return (o as Record<string, unknown>)[mapKey(action.sortcolumn)];
          },
        ]),
        direction: 'ascending',
      };
    case 'FILTER':
      return {
        ...state,
        data: action.data!.filter((d) => {
          return (
            moment(d.orderDate).local().format('YYYY-MM-DD') ===
            moment(action.datevalue).format('YYYY-MM-DD')
          );
        }),
      };
    case 'CLEAR_FILTERS':
      return {
        ...state,
        data: action.data,
        sortcolumn: null,
      };
    case 'UPDATE':
      return {
        ...state,
        data: action.data?.filter((order) => {
          return order?.status !== 'draft';
        }),
      };
    default:
      throw new Error();
  }
}
/**
 * What the orders screen accepts.
 *
 * Established from its four JSX call sites — `components/common/food/index.tsx`
 * lines 348 and 373, and `components/common/foodCard/index.tsx` lines 77 and 98
 * — which between them split on `patientId`: the member-scoped renders pass
 * `patient`/`patientId`/`allReferralData`, the roster-wide renders pass
 * `onPatientSelected` instead and omit the member fields entirely. That split is
 * why those fields are optional here rather than nullable, and it is also what
 * the component's own `if (props.patientId)` branches are testing.
 *
 * Two props are accepted but never read, and are declared here only so their
 * call sites keep compiling:
 *
 * - `programBudget1` — both `food/index.tsx` renders pass it (lines 367 and 387)
 *   alongside the identical `programBudget`.
 * - `allPatientOrders` — `food/index.tsx:363` alone passes it. Nothing in the
 *   application reads that name; the component fetches its own orders through
 *   the `useSWR` below.
 *
 * SUSPECTED DEFECT (components/common/food/index.tsx:363, :367, :387) — REPORTED,
 * NOT FIXED. Both are dead at the boundary. Deleting them from the call sites
 * would be inert today, but "the component ignores it" and "the component was
 * meant to use it" are indistinguishable from the types, and `allPatientOrders`
 * feeding a component that also fetches `/order` itself reads more like an
 * abandoned lift than like deliberate redundancy. Whoever knows which it was
 * should decide; declaring them keeps the contract honest in the meantime.
 */
type PatientOrdersProps = {
  /** Accepted but unread — see the type doc above. */
  allPatientOrders?: FoodOrder[];
  /**
   * Referrals for the member, filtered for the in-progress one to seed
   * `referralId`. Only the member-scoped call sites pass it.
   */
  allReferralData?: ReferralSummary[] | undefined;
  /**
   * `false` suppresses the orders fetch entirely; the check is `!== false`, so
   * an absent prop fetches. Only `foodCard/index.tsx` passes it, and it passes
   * its own `props.fetchOrders` straight through.
   */
  fetchOrders?: boolean | undefined;
  /** Restricts the food-card renders to orders whose vendor is in this list. */
  foodCardVendorIds?: string[];
  /** Set by both `foodCard/index.tsx` renders; switches the `foodTypeCode` filter. */
  foodCardOrdersOnly?: boolean;
  /**
   * Opens the roster-wide member drawer. Only the `patientId === null` call sites
   * pass it, and the component calls it unguarded inside `openSelectedPatient`.
   */
  onPatientSelected?: ((patient: unknown) => void) | undefined;
  /**
   * Routes to the Solutran EBT views. Only `food/index.tsx` passes it.
   *
   * `orderId` is optional because both call sites can supply nothing — this file
   * passes `selectedOrderIds[0]` (`noUncheckedIndexedAccess`) and `./table.tsx`
   * passes an order's optional `orderId`. The producer takes it untyped and only
   * stores it, so absence is carried rather than rejected.
   */
  openSolutranView?: (view: string, orderId?: string) => void;
  /** Accumulated meal-equivalent total, lifted into the food tab. */
  ordersMealEquivalents?: number;
  org?: string | null | undefined;
  /**
   * The member, already flattened by `parseFHIRPatient`. Absent on the
   * roster-wide renders, present on the member-scoped ones — the same split that
   * `patientId` marks, because the four call sites pass or omit them together.
   *
   * Every unguarded `props.patient.org` read in this file sits under that split:
   * `addOrder` and `createEBTPendingOrder` are reachable only from controls
   * rendered inside a `props.patientId &&` branch, and the `<AddOrder>` render
   * itself is in one. Those reads are asserted rather than guarded because the
   * guard already exists — it is just several frames up, where the compiler
   * cannot follow it. (`_getEstimatedDeliveryDate` has no caller at all.)
   */
  patient?: FlatPatient | undefined;
  /** Absent or null selects the roster-wide mode. */
  patientId?: string | null | undefined;
  /**
   * The patient's profile bundle, walked for DELIVERY-PREFERENCES observations.
   * `{}` because the parent seeds the state with an empty object before the
   * `/Observation` fetch resolves, and every read here is `?.entry`.
   */
  profileData?: Bundle | Record<string, never> | undefined;
  programBudget?: ProgramBudget[] | undefined;
  /** Accepted but unread — see the type doc above. */
  programBudget1?: ProgramBudget[];
  programMAXBudget?: number;
  programMAXMealEquivalents?: number;
  programOrdersCost?: number;
  selectedOrg?: string | null | undefined;
  setOrdersMealEquivalents?: (mealEquivalents: number) => void;
  setProgramBudget?: ((budget: ProgramBudget[]) => void) | undefined;
  setProgramMAXBudget?: (budget: number) => void;
  setProgramOrdersCost?: (cost: number) => void;
  /** A vendor code, or the literal 'All'. */
  vendor?: string;
  /** The vendor's display name, uppercased to key `PROGRAM_VENDOR_CONFIG`. */
  vendorName?: string;
};

function PatientOrders(props: PatientOrdersProps) {
  const authInfo = useAuthContext();
  const practitionerName = authInfo.practitionerName;
  // Seeded `''` but `resetFilters` writes `null`, so both are really in it. Read for
  // truthiness and compared `=== '' || === null`, which is why the two producers
  // disagreeing has never shown.
  const [statusvalue, setStatusValue] = useState<string | null>('');
  const [dateValue, setDateValue] = useState<string | null>(null);
  /**
   * SUSPECTED DEFECT — REPORTED, NOT FIXED. Declared `string[]`, but the producer
   * cannot honour that: `selectAll` below maps `state.data.map(d => d.orderId)` and
   * `FoodOrder.orderId` is optional, so selecting all on a page containing an order
   * the feed sent without an id puts `undefined` into the selection.
   *
   * It then flows to `markExported`'s `selectedOrderIds.includes(od.orderId)` filter,
   * which MATCHES every other id-less order, and into `FoodUpdater('/order/' +
   * order.orderId)`, which would address `/order/undefined`. The draft-orders twin
   * has the identical pair of lines; see the note on `selectAll` there.
   *
   * Reconciled to the producer rather than guarded: filtering the absent ids out
   * would silently shrink a select-all the care manager believes covers the page.
   */
  const [selectedOrderIds, setselectedOrderIds] = useState<(string | undefined)[]>([]);
  const [selectedOrdersLength, setselectedOrdersLength] = useState(0);
  const [ordersMessage, setOrdersMessage] = useState('');
  const [working, setWorking] = useState(false);
  const [workingon, setWorkingOn] = useState('');
  const [hideMessage, setHideMessage] = useState(false);
  const [open, setOpen] = useState(false);
  // The draft order being composed in the Add modal. `FoodOrder` declares every
  // member optional, so the `{}` seed is a valid one and matches what `add.tsx`
  // declares it consumes (`orderDetails: FoodOrder`) — that child then tests
  // `foodTypeName === undefined`, `bundleQuantity === undefined` and so on before
  // submitting, which is the same partial-draft assumption stated from the other
  // side of the boundary.
  const [orderDetails, setOrderDetails] = useState<FoodOrder>({});
  const [searchId, setSearchId] = useState('');
  // An order with no `status` becomes a blank entry in the filter dropdown.
  const [statusOptions, setStatusOptions] = useState<(string | undefined)[] | null>(null);
  const [dateState, setdateState] = useState('');
  const [expectedDateState, setExpectedDateState] = useState('');
  const [startRange, setStartRange] = useState('');
  const [endRange, setEndRange] = useState('');
  const [startExpectedDateRange, setStartExpectedDateRange] = useState('');
  const [endExpectedDateRange, setEndExpectedDateRange] = useState('');
  const [sendConfirm, setSendConfirm] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [disableCreatedDateFilter, setDisableCreatedDateFilter] = useState(false);
  const [disableExpectedDueDateFilter, setDisableExpectedDueDateFilter] = useState(false);
  const [ordersList, setOrdersList] = useState<FoodOrder[]>([]);
  const [rawPatients, setRawPatients] = useState<Record<string, unknown> | null>(null);
  const [patients, setPatients] = useState<Record<string, FlatPatient> | null>(null);
  const [estimatedDeliveryDateRange, setEstimatedDeliveryDateRange] = useState(new Date());
  const [_ordersUpdateError, setOrdersUpdateError] = useState(false);
  // Both are written from a `MultiFoodFetcher` dataset element — the same
  // `/food/bundle-item` and `/food/item` responses the local
  // `bundleItemsDataset`/`foodItemsDataset` bindings claim as `FoodItem[]` —
  // and neither is read; the fetched values are consumed through those locals
  // instead. `undefined` joins the union because `MultiFoodFetcher` resolves
  // `undefined` for an endpoint that returned nothing.
  const [_bundleItemsData, setBundleItemsData] = useState<FoodItem[] | null | undefined>(null);
  const [_itemsData, setItemsData] = useState<FoodItem[] | null | undefined>(null);
  // Written from `VendorPriceRow.vendors` and never read — the `_` prefix marks
  // that. Typed from the writer rather than the `{}` seed.
  const [_vendorsFoodPrice, setVendorsFoodPrice] = useState<VendorPricingByCode>({});
  const [_referralDate, _setReferralDate] = useState('');
  const [orderData, setOrderData] = useState<Fetched<FoodOrder[]>>([]);
  const [isTriageModalOpen, setIsTriageModalOpen] = useState(false);
  const [_tempAddProgramOrderCost, _setTempAddProgramOrderCost] = useState(0);
  // Set to the first in-progress referral's id — itself optional on
  // `ReferralSummary` — or `''` when there is none.
  const [referralId, setReferralId] = useState<string | null | undefined>(null);
  const [initialiszeBudgetFlag, setInitialiszeBudgetFlag] = useState(true);

  const PATIENT_ATTRIBUTES = [
    'firstName',
    'lastName',
    'delivery_addressLine1',
    'delivery_addressLine2',
    'delivery_city',
    'delivery_state',
    'delivery_zip',
    'email',
    'homePhone',
    'mobilePhone',
  ];
  const [loading, setLoading] = React.useState(false);
  const [loadingEDDRange, setLoadingEDDRange] = React.useState(true);

  const invoiceStatusList = ['partially-fulfilled', 'unfulfilled', 'delivered', 'reshipped'];

  if (PROGRAM_VENDOR_CONFIG?.[mapKey(props.org)]?.[mapKey(props.vendorName)]?.['PRICE']) {
    const price = PROGRAM_VENDOR_CONFIG?.[mapKey(props.org)]?.[mapKey(props.vendorName)]?.['PRICE'];
    setOrderDetails({
      ...orderDetails,
      foodPricing: price!.DEFAULT_FOOD_PRICE!,
      shippingFees: price!.DEFAULT_SHIPPING_PRICE,
    });
  }
  const estimateDeliveryDateOption = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    onSuccess: (data) => {
      setEstimatedDeliveryDateRange(moment((data as { end?: moment.MomentInput }).end).utc().toDate());
      setLoadingEDDRange(false);
    },
  };
  //pull for every org except ALL
  const {
    data: _estimatedDeliveryDate,
    // SWR types `error` as `any` unless its second generic is given, and supplying
    // one here pins the data generic against FoodFetcher's own. The binding is unread
    // but removing it is a real read fewer.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    error: _estimatedDeliveryDateError,
    mutate: estimatedDeliveryDateMutate,
  } = useSWR(
    props?.patient?.org
      ? props?.vendor?.toUpperCase() !== 'ALL'
        ? [
            `/vendor/estimated-delivery/${props.vendor}?orgId=${props?.patient?.org}`,
            authInfo.token,
          ]
        : null
      : null,
    FoodFetcher,
    estimateDeliveryDateOption,
  );

  // NEW: gate that tells us when parent has provided programBudget at least once
  const [programBudgetHydrated, setProgramBudgetHydrated] = useState(false);

  useEffect(() => {
    // flip the gate as soon as the parent feeds us programBudget (even empty array)
    if (Array.isArray(props.programBudget) && props.programBudget.length > 0) {
      setProgramBudgetHydrated(true);
    }
  }, [props.programBudget]);

  useEffect(() => {
    if (props.patientId != null) {
      // SUSPECTED DEFECT — REPORTED, NOT FIXED. Second of FOUR copies of this
      // predicate, not a twin: `./add.tsx` and `patient/index.tsx` carry two more
      // inline, and `isInProgress` in `../catalog/item/foodBundleItems.tsx` is the
      // fourth, extracted rather than inline with its parameter deliberately left
      // unannotated so the same crash survives there.
      // `ReferralSummary.status` is optional, so a referral row without one throws
      // inside the `filter` and takes the food tab down with it. All four must
      // change together or not at all — guarding one converts a loud crash into a
      // silently empty referral list at that site alone, which bills the order to
      // no referral.
      const referralData = props.allReferralData?.filter(
        (item) => item.status!.toUpperCase() === 'IN-PROGRESS',
      );
      setReferralId(referralData && referralData.length > 0 ? referralData[0]?.referralId : '');
    }

    // Cast, not `!`: `undefined > 0` is `false`, so the chain genuinely gates the
    // loop. Asserting presence would claim the opposite of what this line relies on.
    if ((orderData?.length as number) > 0) {
      let tempMealEquivalents = 0;
      let tempFoodPricing = 0;

      // Use regular for loop instead of async forEach to avoid race conditions
      for (const order of orderData!) {
        // calculate meal equivalents and pricing based on the order status and type
        tempMealEquivalents += calculateMealEquivalents(order, false, props);

        const temp = calculateMealPricing(order, false, props);

        // Only update budget if we have valid data and conditions are met
        if (props.programBudget && props.programBudget.length > 0) {
          if (props.vendor === 'All' && (statusvalue === '' || statusvalue === null)) {
            updateProgramBudgetByReferralId(
              order?.referralId,
              temp,
              props.programBudget,
              props.setProgramBudget!,
            );
          }
          // Unreachable: the `if` above tests the same two conjuncts, so this arm
          // never runs whatever `initialiszeBudgetFlag` holds. Its body is entirely
          // commented out, so nothing is lost — kept because it is upstream's shape.
        } else if (
          // eslint-disable-next-line no-dupe-else-if
          props.programBudget &&
          props.programBudget.length > 0 &&
          !initialiszeBudgetFlag
        ) {
          // setProgramBudgetByReferralId(order?.referralId, 0, props.programBudget,
          // props.setProgramBudget);
          //  setInitialiszeBudgetFlag(false);
        }
        tempFoodPricing += temp;
      }

      // The rounded total is never read; the accumulation is upstream's and stays.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      tempFoodPricing = Math.round(tempFoodPricing * 100) / 100; // Round to 2 decimal places

      if (props.vendor === 'All' && (statusvalue === '' || statusvalue === null)) {
        props.setOrdersMealEquivalents?.(tempMealEquivalents);

        // Set the flag to false after processing all orders
        if (props.programBudget && props.programBudget.length > 0 && initialiszeBudgetFlag) {
          setInitialiszeBudgetFlag(false);
        }
      }
    }
  }, [props.allReferralData, orderData, programBudgetHydrated]);

  //for the delay in fetching the date
  useEffect(() => {
    setLoadingEDDRange(true);
  }, [props?.vendor]);

  const bundleOptions: SWRConfiguration<Fetched<FoodOrderBundle[]>> = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    onSuccess: (_data, _key, _config) => {},
  };

  const {
    data: bundlesData,
    error: _bundlesError,
    isValidating: bundlesLoading,
  } = useSWR<Fetched<FoodOrderBundle[]>, Error>(
    [`/food/bundle`, authInfo.token],
    FoodFetcher,
    bundleOptions,
  );

  // Annotated because SWR 0.5.7 defaults `Data` to `any` and a detached options
  // `const` infers nothing from its `useSWR` call site.
  const ordersDataOptions: SWRConfiguration<Fetched<FoodOrder[]>> = {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
    onSuccess: (data) => {
      setOrderData(data);
      /*
       * SUSPECTED DEFECT — REPORTED, NOT FIXED. `data.length` is read unguarded while
       * every OTHER read of the same value in this callback tests it first
       * (`temp?.filter`, `temp && temp.map`, `if (temp && temp.length)`).
       *
       * SWR 0.5.7 calls `onSuccess(data, key, config)` with whatever the fetcher
       * resolved, and `FoodFetcher` resolves `undefined` — not `[]` — for an empty
       * endpoint. The key here is `'/order' + url`, which is never empty, so the
       * fetcher does reach the API on every call; that is what keeps this from
       * throwing today. It is guarded nowhere and asserted nowhere: the guards three
       * lines down are the same author hedging the same value.
       */
      if (data!.length > 0 && props.vendor === 'All')
        resetProgramOrderCost(props.programBudget, props.setProgramBudget);

      let temp = data;

      temp = temp?.filter((order) => {
        return order.status !== 'draft';
      });
      if (props.foodCardOrdersOnly) {
        temp = temp?.filter((order) => {
          if (!isFoodCardOrder(order)) {
            return false;
          }
          if (props.vendor === 'All' && props.foodCardVendorIds?.length) {
            // Widened rather than asserted: `as string` would claim `vendorId` is
            // present, when an order without one is exactly what this must exclude.
            return (props.foodCardVendorIds as (string | undefined)[]).includes(order.vendorId);
          }
          return true;
        });
      } else {
        temp = temp?.filter((order) => {
          if (isFoodCardOrder(order)) {
            return false;
          }
          // See the mirror-image test in the `foodCardOrdersOnly` arm above.
          if (
            props.foodCardVendorIds?.length &&
            (props.foodCardVendorIds as (string | undefined)[]).includes(order.vendorId)
          ) {
            return false;
          }
          return true;
        });
      }
      if (!statusvalue && !dateValue) {
        setStatusOptions(
          Array.from(
            new Set(
              temp &&
                temp.map((order) => {
                  return order.status;
                }),
            ),
          ),
        );
      }
      setLoading(false);
      dispatch({ type: 'CLEAR_FILTERS', data: temp });
      dispatch({
        type: 'CHANGE_SORT',
        // Never invoked — see the defect note on `OrdersTableAction['sortcolumn']`.
        // `mapKey` names the coercion the subscript already performs; `status` is
        // optional on `FoodOrder`, and `statusColors[undefined]` reads the
        // `'undefined'` key, which is what this expression does today.
        sortcolumn: (od) => {
          return [statusColors[mapKey(od.status)], od.orderDate];
        },
        data: temp,
      });

      if (temp && temp.length) {
        /*
         * SUSPECTED DEFECT — REPORTED, NOT FIXED. `FoodOrder.orderDate` is optional and
         * `new Date(undefined)` is an Invalid Date, whose `getTime()` is `NaN`. A
         * comparator that returns `NaN` gives `Array.prototype.sort` an inconsistent
         * ordering, so ONE undated order does not merely sort to an end — it can leave
         * the surrounding dated rows in an arbitrary order too.
         *
         * The twin in `draftOrders/index.tsx:451` is the same expression. Asserted, not
         * defaulted: choosing where an undated order belongs (newest? oldest?) is a
         * product decision, and `?? 0` would silently pin them all to the epoch.
         */
        temp.sort((a, b) => {
          return new Date(b.orderDate!).getTime() - new Date(a.orderDate!).getTime();
        });
        dispatch({
          type: 'UPDATE',
          data: temp,
        });
        setOrdersList(temp);
        const patientsObj = temp.map((order) => {
          return { id: order.patientId, org: order.patientOrg };
        });
        // The `filter(c => c && …)` below drops the absent ids before they are joined
        // into the search URL.
        const orgPatients: Record<string, (string | undefined)[]> = {};
        patientsObj?.forEach((patient) => {
          if (patient.org) {
            if (Object.prototype.hasOwnProperty.call(orgPatients, patient.org)) {
              // The `hasOwnProperty` test above is what makes this read safe;
              // noUncheckedIndexedAccess cannot see through it.
              orgPatients[patient.org]!.push(patient.id);
            } else {
              orgPatients[patient.org] = [patient.id];
            }
          }
        });
        const urls: string[] = [];
        if (Object.keys(orgPatients).length) {
          Object.keys(orgPatients).forEach((org) => {
            const patients = orgPatients[org]!;
            const uniquePatients = patients.filter((c, index) => {
              if (c && c !== '') return patients.indexOf(c) === index;
            });
            if (uniquePatients && uniquePatients.length) {
              const groups = uniquePatients
                .map((e, i) => {
                  return i % 50 === 0 ? uniquePatients.slice(i, i + 50) : null;
                })
                .filter((e): e is (string | undefined)[] => Boolean(e));
              // eslint-disable-next-line @typescript-eslint/no-for-in-array -- `for...in` over an array is the existing traversal; changing it to `for...of` would alter the index type from string to element and is a behaviour change, not an annotation. The rule became visible only once `groups` stopped being `any`.
              for (const groupIndex in groups) {
                urls.push(`/Patient/?organization=${org}&_id=${groups[groupIndex]!.join(',')}`);
              }
            }
          });
          getPatients(urls);
        } else {
          setLoading(false);
        }
      }
    },
  };

  // SUSPECTED DEFECT: `orderItems` starts as null and is reassigned from
  // `buildOrderItems`, which returns undefined when no bundle item matches. It is then
  // passed to `pricingCalculator` (below), whose first act is `ordersItems.length` —
  // so a non-matching bundle throws here. The `if (orderItems !== null)` guard further
  // down, and the `orderItems?.['foodPricing']` reads inside it, show the code already
  // expects null; the guard is simply placed after the dereference rather than before.
  // Not fixed: moving or widening the guard would convert a crash into a silent skip.
  // SUSPECTED DEFECT — REPORTED, NOT FIXED. Same shape as
  // `food/draftOrders/index.tsx`, which carries the twin of this note.
  // `orderItems` starts `null`, is reassigned from `buildOrderItems` (which
  // returns `undefined` when no bundle item matches), and is written through
  // (`orderItems['foodPricing'] = …`) before the `if (orderItems !== null)` guard
  // below runs — so the guard is downstream of the dereference it was meant to
  // protect. Not fixed: hoisting the guard would convert a crash into a silently
  // skipped price update on the alternate-bundle swap.
  // The signature is the one `orders/table.tsx` already declares for this
  // callback in `OrdersTableProps.updateAlternateOption` — the consumer had
  // written the contract down; only the producer had not.
  async function updateAlternateOption(
    order: FoodOrder,
    alternateFoodOptions: string[],
    selectedAlternate: string,
    bundle: FoodOrderBundle[],
  ) {
    setLoading(true);
    /*
     * An ARRAY, not a record — `buildOrderItems` returns `items.filter(…)`, and
     * `pricingCalculator` declares its third parameter `PricedOrderItem[]` and
     * reads `.length` off it. The twin of this binding in
     * `food/draftOrders/index.tsx` carries the same correction and the same
     * reasoning.
     *
     * The intersection is not decoration: the two `orderItems['foodPricing'] = …`
     * writes below hang NAMED properties off that array. They are readable
     * in-process, but `JSON.stringify` drops named properties from an array, so
     * they do NOT survive into the `FoodUpdater` body — the payload carries them
     * only because it also copies them to its own top-level keys.
     */
    let orderItems:
      | (FoodItem[] & { foodPricing?: number | undefined; shippingFees?: number | undefined })
      | null = null;

    // NOTE: order of urls in this array matter!
    const urls = [`/food/bundle-item?bundleId=${selectedAlternate}`];

    const itemUrl = `/food/item?vendorId=${order.vendorId}`;

    urls.push(itemUrl);

    if (order?.patientOrg) {
      urls.push(`/food/price?organization=ALL`);
    }

    await MultiFoodFetcher(authInfo.token, ...urls).then(async (dataset) => {
      // Positional, per the "order of urls matter" note above. `MultiFoodFetcher`
      // resolves each URL to `unknown[] | undefined`, so the element type is the
      // caller's claim; both endpoints return food items.
      const bundleItemsDataset = dataset[0] as FoodItem[] | undefined;
      const foodItemsDataset = dataset[1] as FoodItem[] | undefined;

      /* Setting Bundle Items Data    */
      setBundleItemsData(bundleItemsDataset);

      /* Setting Food Items Data    */
      setItemsData(foodItemsDataset);

      // `!` keeps the existing throw: `items.filter` on an absent dataset throws
      // inside this `.then`, which is the hang described in the note above.
      orderItems = await buildOrderItems(foodItemsDataset!, bundleItemsDataset);

      // `MultiFoodFetcher` resolves each URL to `unknown[] | undefined`, so the
      // `/food/price` row arrives untyped. `VendorPriceRow` names the one field read
      // off it; the guard below is unchanged and still tests `vendors` before use.
      const priceRow = dataset[2]?.[0] as VendorPriceRow | undefined;
      if (dataset[2] && dataset[2]?.length && priceRow?.vendors) {
        setVendorsFoodPrice(priceRow?.vendors);
        const validPrice = pricingCalculator(
          order?.patientOrg as string,
          props.vendor!,
          orderItems,
          priceRow?.vendors,
          bundle[0]?.bundleName,
        );
        orderItems['foodPricing'] = validPrice?.foodPrice;
        orderItems['shippingFees'] = validPrice?.shippingFees;
      }

      if (orderItems !== null) {
        const _alternateOptions = alternateFoodOptions.filter((item) => item !== selectedAlternate);
        const orderUpdate = {
          patientId: order.patientId,
          docId: order.orderId,
          orderName: selectedAlternate,
          bundleId: selectedAlternate,
          orderItems: orderItems,
          lastUpdated: moment().utc().format(),
          lastUpdatedBy: authInfo.practitionerName,
          foodPricing: orderItems?.['foodPricing'],
          shippingFees: orderItems?.['shippingFees'],
          status: 'new',
          orderType: 'CATALOG',
          bundleTypeCode: bundle[0]?.bundleTypeCode,
          bundleTypeName: bundle[0]?.bundleTypeName,
          flavorCode: bundle[0]?.flavorCode,
          flavorName: bundle[0]?.flavorName,
          foodTypeCode: bundle[0]?.bundleTypeCode,
          foodTypeName: bundle[0]?.bundleTypeName,
          bundleName: bundle[0]?.bundleName,
          vendorBundleId: bundle[0]?.vendorBundleId,
          bundleEffectivePeriod: {
            start: bundle[0]?.effectivePeriod?.start,
            end: bundle[0]?.effectivePeriod?.end,
          },
          bundleQuantity: 1,
        };

        await FoodUpdater(`/order/` + order.orderId!.toString(), authInfo.token, orderUpdate).then(
          () => {},
        );
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; a rejection only leaves the view stale
        ordersMutate();
        setLoading(false);
        setOrdersUpdateError(false);
      } else {
        setOrdersUpdateError(true);
        setLoading(false);
      }
    });
  }

  // The twin of this function in `food/draftOrders/index.tsx` carries the same
  // signature. The return is DECLARED rather than inferred so `orderItems`
  // above resolves to an array rather than to the filter's widened element type.
  // Declared async so callers get a promise; removing async changes the return type from a promise
  // to a value.
  // eslint-disable-next-line @typescript-eslint/require-await
  async function buildOrderItems(
    items: FoodItem[],
    bundleItemsData: FoodItem[] | undefined,
  ): Promise<FoodItem[]> {
    const preparedOrderItems = items.filter((item) => {
      let itemPayload: FoodItem | undefined;
      bundleItemsData?.forEach((bundleItem) => {
        if (bundleItem.itemId === item.itemId) {
          item.quantity = bundleItem.quantity;
          itemPayload = item;
        }
      });
      return itemPayload;
    });
    return preparedOrderItems;
  }

  // `string[]` from the consumer: `urls` is spread straight into
  // `MultiFetcher(token, ...urls: string[])`.
  const getPatients = (urls: string[]) => {
    MultiFetcher<Bundle>(authInfo.token, ...urls).then(
      (patientsDataset) => {
        // These accumulate into id-keyed maps via the object spreads below, so they are records.
        let combinedPatients: Record<string, FlatPatient> = {};
        let combinedRawPatients: Record<string, unknown> = {};
        patientsDataset?.forEach((patientsData) => {
          if (patientsData && patientsData.entry && patientsData.entry.length) {
            const tempPatients: Record<string, FlatPatient> = {};
            const tempRawPatients: Record<string, unknown> = {};
            patientsData.entry.forEach((item) => {
              // `resource` is `0..1` and its `id` likewise. Asserted rather than
              // narrowed with `bundleEntriesOf`, which would SKIP a malformed entry —
              // silently dropping a member from the roster, where today the parse
              // throws. An absent `id` keys `'undefined'`, as it already did.
              tempPatients[item.resource!.id!] = FHIRUtils.parseFHIRPatient(
                item.resource as Patient,
              );
              tempRawPatients[item.resource!.id!] = item.resource;
            });
            combinedPatients = { ...combinedPatients, ...tempPatients };
            combinedRawPatients = {
              ...combinedRawPatients,
              ...tempRawPatients,
            };
            setLoading(false);
          }
        });
        setPatients(combinedPatients);
        setRawPatients(combinedRawPatients);
      },
      (_error) => {},
    );
  };

  let url = '';
  if (statusvalue) {
    url = '?status=' + statusvalue;
    if (props.foodCardOrdersOnly) {
      url += '&foodTypeCode=FC';
    } else {
      url += '&foodTypeCode:ne=FC';
    }
  } else if (props.foodCardOrdersOnly) {
    url = '?foodTypeCode=FC';
  } else {
    url = '?foodTypeCode:ne=FC';
  }

  if (props.patientId) {
    if (props.vendor === 'All') {
      url += `&patientId=` + props.patientId;
    } else {
      url += `&vendorId=` + props.vendor + `&patientId=` + props.patientId;
    }
  } else if (props.vendor !== 'All') {
    url += `&vendorId=` + props.vendor;
  }

  url +=
    (searchId ? '&orderId=' + searchId : '') +
    (startRange && !startExpectedDateRange
      ? '&createdDate:ge=' +
        moment(startRange).utc().format('YYYY-MM-DD') +
        (endRange
          ? '&createdDate:le=' + moment(endRange).utc().add(1, 'days').format('YYYY-MM-DD')
          : '')
      : '') +
    (!startRange && startExpectedDateRange
      ? '&estimatedDeliveryDate:ge=' +
        moment(startExpectedDateRange).utc().format('YYYY-MM-DD') +
        (endExpectedDateRange
          ? '&estimatedDeliveryDate:le=' +
            moment(endExpectedDateRange).utc().add(1, 'days').format('YYYY-MM-DD')
          : '')
      : '') +
    (props.selectedOrg !== 'All' && !props.patientId ? '&patientOrg=' + props.selectedOrg : '');

  const shouldFetchOrders = props.fetchOrders !== false;

  const {
    data: ordersData,
    error: ordersError,
    mutate: ordersMutate,
    isValidating: datasetLoading,
  } = useSWR<Fetched<FoodOrder[]>, Error>(
    shouldFetchOrders ? [`/order` + url, authInfo.token] : null,
    FoodFetcher,
    ordersDataOptions,
  );

  // Third instance of the undated-order comparator; see the SUSPECTED DEFECT note on
  // the one inside `ordersDataOptions.onSuccess`. Cast rather than `!`: an undated
  // order really does reach here, and `new Date(undefined)` yields Invalid Date,
  // whose NaN comparison is the sort behaviour being preserved.
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- short-circuit used as a statement; the `if` that satisfies the rule is a different expression
  ordersData &&
    ordersData?.sort((a, b) => {
      return new Date(b?.orderDate as string)?.getTime() - new Date(a?.orderDate as string)?.getTime();
    });

  // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- short-circuit used as a statement; the `if` that satisfies the rule is a different expression
  ordersData &&
    ordersData?.map((order) => {
      const quantity =
        order.orderItems &&
        order.orderItems.length > 0 &&
        _.sum(
          order.orderItems.map((od) => {
            return parseInt(od.quantity);
          }),
        );
      order.quantity = quantity
        ? quantity
        : order.quantity
          ? order.quantity
          : order.bundleQuantity
            ? order.bundleQuantity
            : 1;
      // `FlatPatient.firstName` is the fhirpath result array (`name.given.first()`),
      // assigned into a `string` field. Harmless: every consumer coerces, and
      // `.first()` caps it at one element, so `String(['Jane'])` is `'Jane'`.
      order.firstName = order.patientId && patients ? (patients[order.patientId]?.firstName as unknown as string) : '';
    });
  const [state, dispatch] = React.useReducer(Reducer, {
    sortcolumn: null,
    data: null,
    direction: null,
  });
  // SUSPECTED DEFECT — REPORTED, NOT FIXED. The reducer writes the sorted
  // column under `sortcolumn`; nothing ever writes `column`. So `column` is
  // `undefined` for the component's whole life, and `column={column}` at the
  // `<OrdersTable>` render below makes every one of the table's
  // `props.column === '<key>' ? props.direction : null` header tests false —
  // the sort still happens, but no header ever shows its ascending/descending
  // caret. `direction` and `data` do resolve, which is why the bug reads as
  // purely cosmetic and has survived.
  //
  // The identical destructure sits at `../draftOrders/index.tsx:671` and
  // `../foodRecommendations/index.tsx:332` against reducers with the same
  // `sortcolumn` field, so all three tables are missing the same indicator.
  //
  // `column` is left destructured (as `undefined`) rather than renamed to
  // `sortcolumn`, because renaming it would switch the carets on — a visible
  // change to what the software does, which this pass may not make.
  const { data, direction } = state;
  const column: string | undefined = undefined;
  // Assigned from `props.patient` here and from `patients[order.patientId]`
  // (a `Record<string, FlatPatient>`) in the simulate/send loops, so the
  // declaration must carry the union of both plus the unassigned case.
  let flatPatient: FlatPatient | undefined;
  if (props.patientId) {
    flatPatient = props.patient;
  }

  useEffect(() => {
    dispatch({ type: 'CLEAR_FILTERS', data: ordersList });
    setOrdersMessage('');
    setselectedOrderIds([]);
    setselectedOrdersLength(0);
  }, [ordersList]);

  // SUSPECTED DEFECT — REPORTED, NOT FIXED. `onPatientSelected` belongs to the
  // roster-wide mode and is passed only by the two renders that omit `patientId`;
  // the member-scoped renders leave it out. The guard here tests `rawPatients`,
  // which is the wrong side — it establishes that there is a member to open, not
  // that there is a handler to open it with. The member name in a row is only
  // clickable in roster-wide mode today, so the two coincide; they stop
  // coinciding the moment that column is shown member-scoped.
  const openSelectedPatient = (id?: string) => {
    if (rawPatients) {
      props.onPatientSelected!(rawPatients[mapKey(id)]);
    }
  };
  const resetFilters = () => {
    setStatusValue(null);
    setDateValue(null);
    setdateState('');
    setStartRange('');
    setEndRange('');
    setSearchId('');
    setEndExpectedDateRange('');
    setStartExpectedDateRange('');
    setExpectedDateState('');
    dispatch({ type: 'CLEAR_FILTERS', data: ordersList });
    setDisableCreatedDateFilter(false);
    setDisableExpectedDueDateFilter(false);
  };

  // `state.data` is non-null on both handlers below for the same structural
  // reason: each is reachable only from a control inside `<OrdersTable>`, and that
  // table is rendered behind `!isLoading && !ordersError`, by which point the
  // dataset effect has dispatched `CLEAR_FILTERS` with an array. The reducer's
  // seed type stays nullable, so the compiler cannot see it.
  // Signature taken from the consumer: `orders/table.tsx` declares this prop as
  // `(event: React.FormEvent<HTMLInputElement>, checkprops: StrictCheckboxProps)`
  // and hands it straight to a semantic-ui `Checkbox onChange`.
  const selectAll = (_e: React.FormEvent<HTMLInputElement>, checkprops: StrictCheckboxProps) => {
    if (checkprops.checked) {
      setselectedOrderIds(
        state.data!.map((d) => {
          return d.orderId;
        }),
      );
      setselectedOrdersLength(state.data!.length);
    } else {
      setselectedOrderIds([]);
      setselectedOrdersLength(0);
    }
  };
  async function markExported() {
    setWorking(true);
    const temp = state.data!.filter((od) => {
      return selectedOrderIds.includes(od.orderId);
    });
    for (let i = 0; i < temp.length; i++) {
      const order = temp[i]!;
      if (
        order.status &&
        (order.status.toLowerCase() === 'new' || order.status.toLowerCase() === 'requested')
      ) {
        const orderUpdate = {
          patientId: order.patientId,
          docId: order.orderId,
          status: 'exported',
          exportedBy: authInfo.practitionerName,
          exportFile: props.vendor + '-' + moment().utc().format(),
          exportedDate: moment().utc().format(),
          lastUpdated: moment().utc().format(),
          lastUpdatedBy: authInfo.practitionerName,
        };
        // SUSPECTED DEFECT — REPORTED, NOT FIXED. `FoodOrder.orderId` is optional
        // and `.toString()` throws on absence — the one coercion in this app that
        // is not total (contrast `utils/mapKey`, which exists for exactly this).
        // An order that reached the table without an id fails the export mid-loop,
        // after earlier orders in the same batch were already marked exported, so
        // the batch ends partially applied and the message below never runs.
        await FoodUpdater(`/order/` + order.orderId!.toString(), authInfo.token, orderUpdate).then(
          () => {},
        );
      }
    }
    setOrdersMessage('Order Exported Successfully!');
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; a rejection only leaves the view stale
    ordersMutate();
    setWorking(false);
  }
  function addExportData(e: unknown, checkprops: StrictCheckboxProps, orderId?: string) {
    let temp = selectedOrderIds;
    if (checkprops.checked) {
      temp.push(orderId);
    } else {
      temp = temp.filter((o) => {
        return o !== orderId;
      });
    }
    setselectedOrderIds(temp);
    setselectedOrdersLength(temp.length);
  }
  function _simulateOrders() {
    setWorking(true);
    setHideMessage(false);
    setWorkingOn('Simulating');
    const temp =
      ordersList &&
      ordersList.filter((od) => {
        // `FoodOrder.orderId` is optional, and so is the selection's element type —
        // see the SUSPECTED DEFECT note on `selectedOrderIds`. An id-less order here
        // matches every OTHER id-less order rather than being excluded.
        return selectedOrderIds.includes(od.orderId);
      });
    setOrdersMessage('Simulating ' + temp.length.toString() + ' orders...');
    temp.map((order) => {
      const payload: OrderPostPayload = {};
      payload['order'] = order;
      // Every branch below writes a string, so the accumulator is a string map
      // keyed by the `PATIENT_ATTRIBUTES` names.
      const patientAtt: Record<string, string> = {};
      PATIENT_ATTRIBUTES.map((attribute) => {
        if (!flatPatient) {
          // `FoodOrder.patientId` is optional, so an order without one looks up
          // the key "undefined". `patients && ...` still yields `null` when
          // `patients` is null, which is why the assertion covers the whole
          // expression rather than adding a fallback.
          flatPatient = (patients && patients[order.patientId as string]) as
            | FlatPatient
            | undefined;
        }
        const value = flatPatient && (flatPatient as unknown as Record<string, unknown[]>)[attribute];
        if (typeof value === 'string') {
          patientAtt[attribute] = value;
        } else {
          if (value) {
            // The non-string branch takes element 0 of an array-valued field
            // without checking the array is non-empty.
            patientAtt[attribute] = (value as string[])[0] as string;
          } else {
            patientAtt[attribute] = '';
          }
        }
      });
      payload['patient'] = patientAtt;
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget write; a rejection is dropped and the user sees no failure
      FoodPoster<FoodOrderSubmitResponse>(`/order/sb?simulate=true`, authInfo.token, payload).then(
        (response) => {
          // Deliberate string coercion of a deliberately wide declared type; the runtime values are
          // scalars.
          // eslint-disable-next-line @typescript-eslint/no-base-to-string
          if (response.status && response.status.toString().toLowerCase() === 'accepted') {
            setOrdersMessage('Simulation Successful!');
          } else {
            if (response.errorType) {
              setOrdersMessage(
                'Simulation Error- ' + response.errorType + ': ' + response.errorMessage,
              );
            } else {
              setOrdersMessage('Simulation Error- ' + response.title + ': ' + response.detail);
            }
          }
          setWorking(false);
        },
      );
    });
  }
  function sendOrders() {
    setWorking(true);
    setHideMessage(false);
    setWorkingOn('Sending');
    const temp =
      ordersList &&
      ordersList.filter((od) => {
        // `FoodOrder.orderId` is optional, and so is the selection's element type —
        // see the SUSPECTED DEFECT note on `selectedOrderIds`. An id-less order here
        // matches every OTHER id-less order rather than being excluded.
        return selectedOrderIds.includes(od.orderId);
      });
    setOrdersMessage('Sending ' + temp.length.toString() + ' orders...');
    temp.map((order) => {
      const payload = {};
      payload['order'] = order;
      const patientAtt = {};
      PATIENT_ATTRIBUTES.map((attribute) => {
        if (!flatPatient) {
          // `FoodOrder.patientId` is optional, so an order without one looks up
          // the key "undefined". `patients && ...` still yields `null` when
          // `patients` is null, which is why the assertion covers the whole
          // expression rather than adding a fallback.
          flatPatient = (patients && patients[order.patientId as string]) as
            | FlatPatient
            | undefined;
        }
        const value = flatPatient && (flatPatient as unknown as Record<string, unknown[]>)[attribute];
        if (typeof value === 'string') {
          patientAtt[attribute] = value;
        } else {
          patientAtt[attribute] = (value as unknown[])[0];
        }
      });
      payload['patient'] = patientAtt;

      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget write; a rejection is dropped and the user sees no failure
      FoodPoster<FoodOrderSubmitResponse>(`/order/sb`, authInfo.token, payload).then((response) => {
        // Deliberate string coercion of a deliberately wide declared type; the runtime values are
        // scalars.
        // eslint-disable-next-line @typescript-eslint/no-base-to-string
        if (response.status && response.status.toString().toLowerCase() === 'accepted') {
          setOrdersMessage('Sending Successful!');
        } else {
          if (response.errorType) {
            setOrdersMessage('Sending Error- ' + response.errorType + ': ' + response.errorMessage);
          } else if (response.status && response.status === 'unaccepted') {
            setOrdersMessage('Sending Error- ' + response.title + ': ' + response.detail);
          } else {
            setOrdersMessage('Sending Error- ' + response.title + ': ' + response.detail);
          }
        }
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; a rejection only leaves the view stale
        ordersMutate();
        setWorking(false);
        setSendConfirm(false);
      });
    });
  }
  function cancelOrders() {
    setWorking(true);
    setHideMessage(false);
    setWorkingOn('Cancelling');
    const temp =
      ordersList &&
      ordersList.filter((od) => {
        // `FoodOrder.orderId` is optional, and so is the selection's element type —
        // see the SUSPECTED DEFECT note on `selectedOrderIds`. An id-less order here
        // matches every OTHER id-less order rather than being excluded.
        return selectedOrderIds.includes(od.orderId);
      });
    setOrdersMessage('Cancelling ' + temp.length.toString() + ' orders...');
    temp.map((order) => {
      // if (props.vendor === 'SB') {
      if (order.vendorId === 'SB') {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget write; a rejection is dropped and the user sees no failure
        FoodDelete(
          `/order/sb/` + order.vendorOrderId + `?orderId=` + order.orderId,
          authInfo.token,
        ).then((response) => {
          if (response === '') {
            setOrdersMessage('Cancellation Successful!');
          } else {
            setOrdersMessage(response.title + ': ' + response.detail);
          }
          setWorking(false);
        });
      } else {
        // The cancellation body sent to `/order/{id}`: five string fields, all
        // assigned unconditionally below.
        const payload: Record<string, string> = {};
        payload['status'] = 'CANCELLED';
        payload['cancelledDate'] = new Date().toISOString();
        payload['cancelledBy'] = authInfo.practitionerName;
        payload['lastUpdated'] = new Date().toISOString();
        payload['lastUpdatedBy'] = authInfo.practitionerName;
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget write; a rejection is dropped and the user sees no failure
        FoodUpdater(`/order/` + order.orderId, authInfo.token, payload).then((response) => {
          if (response.statusCode === '200') {
            const mealEquivalents = calculateMealEquivalents(order, false, props);
            // SUSPECTED DEFECT — REPORTED, NOT FIXED. `ordersMealEquivalents`
            // and `programOrdersCost` reach us as numbers: both originate in
            // `components/common/food/index.tsx:39-40` as `useState(0)` and are
            // only ever set from arithmetic. Running `parseInt` over a number
            // stringifies it and then truncates at the decimal point, so a
            // fractional meal-equivalent total is silently floored on every
            // cancellation; `parseFloat` on a number is a no-op round trip.
            // The casts preserve exactly that behaviour rather than dropping
            // the calls, because whether the flooring is load-bearing for the
            // budget figures shown to care managers is a product question.
            // Same pair at lines 1077-1080 on the create path.
            props.setOrdersMealEquivalents!(
              parseInt(props.ordersMealEquivalents as unknown as string) - mealEquivalents,
            );
            const tempMealPrice = calculateMealPricing(order, false, props);
            const tempProgramOrdersCost =
              Math.round(
                (parseFloat((props?.programOrdersCost ?? 0) as unknown as string) -
                  parseFloat(tempMealPrice.toString())) *
                  100,
              ) / 100; // Round to 2 decimal places

            props.setProgramOrdersCost!(tempProgramOrdersCost);
            setOrdersMessage('Order Cancelled Successfully!');
          } else {
            setOrdersMessage('Cancellation Error - ' + response.message);
          }
          // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; a rejection only leaves the view stale
          ordersMutate();
          setCancelConfirm(false);
          setWorking(false);
        });
      }
    });
  }
  /*
   * The Add-modal field writer, one branch per `attributeName`.
   *
   * Parameter types are reconciled to the call sites in `add.tsx`, which are of
   * two kinds: semantic-ui dropdowns passing `(e, 'name', prop.value)` where
   * `prop.value` is `DropdownProps['value']`, and plain inputs passing `(e, name)`
   * where the value arrives on `e.target` instead. That is why `e` names only the
   * two `target` members the body reads and why `value` is the dropdown union
   * rather than `string` — the branches that call `.toUpperCase()` / `parseInt`
   * on it assert the string arm at their own site.
   *
   * `e` itself is optional: `add.tsx:253` calls `props.handleChange(e, name)`
   * from a handler whose own `e` may be undefined.
   */
  const handleChange = (
    e: { target?: { value?: string; valueAsNumber?: number } } | undefined,
    attributeName: string,
    value?: DropdownProps['value'],
    previous?: OrderNote[],
  ) => {
    const temp = orderDetails;
    /*
     * `as string` / `as number` throughout this body: every branch assumes the
     * arm of the `value` union or the `e.target` member that its own
     * `attributeName` implies, and each of those assumptions is unguarded today.
     * The assertions state them without adding checks — a `?.` or a fallback here
     * would silently write a wrong field value instead of throwing.
     *
     * The `e.target && ...` conjunctions are existing runtime guards and are
     * left exactly as written.
     */
    if (attributeName === 'status' && (value === 'NEW' || value === 'EXPORTED')) {
      temp[attributeName] = value.toLowerCase();
    } else if (attributeName === 'bundleQuantity') {
      temp[attributeName] = (e!.target &&
        e!.target.valueAsNumber &&
        e!.target.valueAsNumber) as number;
      temp['quantity'] = temp[attributeName];
    } else if (
      attributeName === 'foodPricing' ||
      attributeName === 'shippingFees' ||
      attributeName === 'estimatedDeliveryDate'
    ) {
      temp[attributeName] = (e!.target && e!.target.value) as string;
    } else if (attributeName === 'noteText') {
      let notepayload: OrderNote[] = [];
      if (previous) {
        notepayload = [...previous];
      }
      const currentPayload: OrderNote = {
        author: practitionerName,
        time: moment().utc().format(),
        text: (e!.target && e!.target.value) as string,
      };
      notepayload.push(currentPayload);
      temp['orderNotes'] = [...notepayload];
    } else if (attributeName === 'allergens') {
      const allergyPayload: string | undefined = e!.target && e!.target.value;
      // `FoodOrder.allergens` is `?: string`, which under
      // `exactOptionalPropertyTypes` admits omission but not a present
      // `undefined`. This assignment really can store `undefined` — that is what
      // the local's own type says — so the cast records the gap rather than
      // adding a guard that would change which orders carry the key at all.
      temp[attributeName] = allergyPayload as string;
    } else if (attributeName === 'mealEquivalents') {
      if (value !== null && value !== 'NA') {
        temp[attributeName] = parseInt(value as string);
      } else {
        temp[attributeName] = 0;
      }
    } else if (attributeName === 'dtcOrderReason') {
      if (value !== null && value !== '') {
        temp[attributeName] = value as string;
      }
    } else if (attributeName === 'foodTypeName') {
      /*
       * `foodNameMap` and `foodTypeMap` are open-key maps, so a lookup yields
       * `string | undefined` and an unrecognised bundle name writes `undefined`
       * into these fields. `as string` records that these writes are unguarded;
       * the alternative — dropping the key when the lookup misses — is a
       * different behaviour, because `add.tsx` gates submission on
       * `foodTypeName === undefined` and would then see a MISSING key instead of
       * a present undefined one. Both read as absent to `===`, so this is stated
       * rather than changed.
       */
      temp[attributeName] = value as string;
      temp['bundleTypeName'] = foodNameMap[(value as string).toUpperCase()] as string;
      temp['foodTypeCode'] = foodTypeMap[(value as string).toUpperCase()] as string;
      temp['bundleTypeCode'] = temp['foodTypeCode'];
    } else if (attributeName === 'bundleName') {
      //double check vendorConfig.ts if somehting needs to be upper case if something isn't working here.
      // Same open-key lookups as the `foodTypeName` branch above.
      temp[attributeName] = value as string;
      temp['bundleTypeName'] = foodNameMap[(value as string).toUpperCase()] as string;
      temp['foodTypeName'] = foodNameMap[(value as string).toUpperCase()] as string;
      temp['foodTypeCode'] = foodTypeMap[(value as string).toUpperCase()] as string;
      temp['bundleTypeCode'] = temp['foodTypeCode'];

      // Hoisted so the `hasOwnProperty` test and the two reads use one key. The
      // test establishes presence but does not narrow, which is why the reads are
      // written through the open-key `?.` rather than assuming the entry.
      const orgKey = mapKey(props.org);
      const program =
        Object.prototype.hasOwnProperty.call(PROGRAM_VENDOR_CONFIG, orgKey) &&
        // SUSPECTED DEFECT — REPORTED, NOT FIXED. `vendorName` is optional on this
        // component's props and `.toUpperCase()` throws on absence, so selecting a
        // bundle name for a vendor whose display name never resolved fails the
        // change handler and leaves the draft order's price fields unwritten.
        PROGRAM_VENDOR_CONFIG[orgKey]?.[props.vendorName!.toUpperCase()]
          ? PROGRAM_VENDOR_CONFIG[orgKey]
          : // `DEFAULT` resolves through the literal branch of the intersection, whose
            // per-vendor value types differ, so a computed subscript on it is an error type.
            (PROGRAM_VENDOR_CONFIG['DEFAULT'] as Record<string, Record<string, VendorDefaultPrice | undefined> | undefined>);
      const vendorObj = program?.[props.vendorName!.toUpperCase()];
      /*
       * `price` holds a `VendorDefaultPrice` from `PROGRAM_VENDOR_CONFIG`: either
       * the per-bundle entry looked up by bundle name, or the vendor's `PRICE`
       * fallback. Both reads below already go through `?.` because either lookup
       * can miss. The `0` seed is never read — every path reassigns first — but
       * it is in the type because it is what the declaration initialises.
       */
      let price: VendorDefaultPrice | undefined | 0 = 0;
      if (
        vendorObj != null &&
        Object.prototype.hasOwnProperty.call(vendorObj, (value as string)?.toUpperCase())
      ) {
        price = vendorObj[(value as string)?.toUpperCase()];
      } else {
        price = (vendorObj as { PRICE?: VendorDefaultPrice } | undefined)?.PRICE;
      }
      // `?.` on a missing config entry yields `undefined`, and both fields are
      // `?: string | number`, which under `exactOptionalPropertyTypes` does not
      // admit a present `undefined`. The writes really can clear the price fields —
      // which is the behaviour the `SUSPECTED DEFECT` note above describes.
      temp['foodPricing'] = price?.DEFAULT_FOOD_PRICE as number;
      temp['shippingFees'] = price
        ?.DEFAULT_SHIPPING_PRICE as number;

      // setOrderDetails({
      //   ...orderDetails,
      //   foodPricing: price.DEFAULT_FOOD_PRICE,
      //   shippingFees: price.DEFAULT_SHIPPING_PRICE,
      // });
    } else {
      // The open branch: `attributeName` is any other `FoodOrder` key, so the
      // write is through the index and the value is whatever the caller supplied.
      (temp as Record<string, unknown>)[attributeName] = value;
    }
    if (temp['status'] && invoiceStatusList.includes(temp['status'].toLowerCase())) {
      temp['invoiceReady'] = '1';
    }

    setOrderDetails({
      ...temp,
    });
  };

  /*
   * Contract from `add.tsx`, which declares this prop as
   * `(event: unknown, estimatedDeliveryDate: { value?: unknown })` and calls it
   * from a datepicker `onChange`. `value` is narrowed to `moment.MomentInput`
   * here because that is what the body requires — `moment(unknown)` matches no
   * overload — and the datepicker supplies a `Date`.
   */
  const handleEstimateDeliveryDateChange = (
    _e: unknown,
    estimatedDeliveryDate: { value?: moment.MomentInput },
  ) => {
    const temp = orderDetails;
    temp['estimatedDeliveryDate'] = moment(estimatedDeliveryDate.value).format('YYYY-MM-DD');
    setOrderDetails(temp);
  };

  function addOrder(e: {
    target: Iterable<{ name: string; value: string; checked: boolean; readOnly: boolean }>;
  }) {
    setOrdersMessage('Creating Order...');
    setOpen(false);
    setWorking(true);
    setHideMessage(false);
    setWorkingOn('Creating');
    // add build order from FoodBundleItems
    const temp: Record<string, string | number | undefined | null> = {};
    let ebtPendingOrder = false;
    for (const input of e.target) {
      if (input.name === 'ebtPending') {
        ebtPendingOrder = input.checked;
      }

      if (
        (!input.readOnly || input.name === 'estimatedDeliveryDate') &&
        input.name !== '' &&
        input.name !== 'noteText'
      ) {
        if (input.name === 'estimatedDeliveryDate') {
          temp[input.name] = moment(input.value).format('YYYY-MM-DD');
        } else {
          temp[input.name] = input.value;
        }
      }
    }

    const temp1 = orderDetails;
    if (Object.keys(temp1).length > 0) {
      for (const key of Object.keys(temp1)) {
        temp[key] = (temp1 as Record<string, string | number | undefined | null>)[key];
      }
    }

    const deliveryPreferences: string[] = [];

    // `BundleEntry` — `profileData` is a `/Observation` search bundle, so
    // `entry` is `BundleEntry[]` and `resource` is `0..1` and unioned. The `!`
    // preserves the existing throw on a resource-less entry, and the
    // `Observation` assertion is the single-type invariant the query
    // establishes, which `parseFHIRPreference` requires.
    props?.profileData?.entry?.forEach((entry: BundleEntry) => {
      const parsedObservations = FHIRUtils.parseFHIRPreference(entry.resource! as Observation);
      if (parsedObservations && parsedObservations.code === 'DELIVERY-PREFERENCES') {
        deliveryPreferences.push(parsedObservations.valueString);
      }
    });

    // Business wants delivery preferences the same order they created.
    temp['deliveryInstructions'] = deliveryPreferences.reverse().join(' | ');

    setOrdersMessage('Creating order...');
    temp['lastUpdated'] = moment().utc().format();
    temp['lastUpdatedBy'] = authInfo.practitionerName;
    temp['orderDate'] = moment().utc().format();
    temp['createdDate'] = moment().utc().format();
    temp['createdBy'] = authInfo.practitionerName;
    //orderName is bunldeName in table. No bundleName or bundleId for orders created from table
    temp['patientId'] = props.patientId;
    temp['patientOrg'] = props.patient!.org;
    const docId = moment().unix().toString() + (Math.floor(Math.random() * 8999) + 1000);
    temp['orderId'] = docId;
    temp['docId'] = docId;
    temp['vendorName'] = props.vendorName;
    temp['vendorId'] = props.vendor;

    // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- short-circuit used as a statement; the `if` that satisfies the rule is a different expression
    referralId && (temp['referralId'] = referralId);

    if (!ebtPendingOrder) {
      temp['status'] = 'new';
      if (temp['foodTypeName'] === undefined) {
        temp['foodTypeCode'] = foodTypeMap[(temp['bundleName'] as string).toUpperCase()];
      } else {
        temp['foodTypeCode'] = foodTypeMap[(temp['foodTypeName'] as string).toUpperCase()];
      }
      //if bundleName in used in order, add bundleName as orderName
      temp['orderName'] =
        temp['bundleName'] && temp['bundleName'] !== undefined
          ? temp['bundleName']
          : temp['flavorName']
            ? temp['foodTypeName'] + '_' + temp['flavorName']
            : temp['foodTypeName'];
    } else {
      temp['status'] = 'EBT PENDING';
    }
    // Cristina wants Gap orders to be considered for MEQs - 07152024
    if (temp['status'] === 'EBT PENDING') {
      temp['mealEquivalents'] = 0;
    }

    setLoading(true);

    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget write; a rejection is dropped and the user sees no failure
    FoodPoster<FoodOrderCreateResponse>(`/order/`, authInfo.token, temp).then((response) => {
      setWorking(true);
      setLoading(false);
      if (response.statusCode === '201') {
        // props.setOrdersMealEquivalents(temp['mealEquivalents'] +
        // parseInt(props.ordersMealEquivalents));

        const mealEquivalents = calculateMealEquivalents(temp, false, props);
        // See the SUSPECTED DEFECT note on the cancel path above: same
        // `parseInt`/`parseFloat` over values that are already numbers.
        props.setOrdersMealEquivalents!(
          parseInt(props.ordersMealEquivalents as unknown as string) + mealEquivalents,
        );
        const tempMealPrice = calculateMealPricing(temp, false, props);
        const tempProgramOrdersCost =
          Math.round(
            (parseFloat((props?.programOrdersCost ?? 0) as unknown as string) +
              parseFloat(tempMealPrice.toString())) *
              100,
          ) / 100; // Round to 2 decimal places
        props.setProgramOrdersCost!(tempProgramOrdersCost);
        setProgramBudgetByReferralId(
          referralId!,
          tempProgramOrdersCost,
          props.programBudget!,
          props.setProgramBudget!,
        );
        setOrdersMessage('Order Created Successfully!');
        setWorking(false);
      } else {
        setOrdersMessage('Error - ' + response.errorMessage);
        setWorking(false);
      }
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; a rejection only leaves the view stale
      ordersMutate();
      setOrderDetails({});
      setLoading(false);
      setWorking(false);
    });
  }

  /**
   * Fetches the vendor's estimated delivery window from the foodcourt API.
   *
   * The name is a misnomer: this hits `VITE_FOODCOURT_API_NAME`, not the FHIR
   * proxy, and returns a date range rather than a FHIR resource.
   *
   * `{ end?: string }` is an unchecked claim; the response is unvalidated JSON.
   * Only `end` is read, by `moment(data.end)` in `_getEstimatedDeliveryDate`.
   */
  const FHIRFetcherOnDemand = async (endPoint: string, token: string) => {
    const dataEndPoint = endPoint;

    const data = await apiClient.get<{ end?: string }>(
      import.meta.env.VITE_FOODCOURT_API_NAME,
      dataEndPoint,
      {
        headers: {
          Authorization: 'Bearer ' + token,
          'Content-Type': 'application/json',
        },
      },
    );
    return data;
  };

  async function _getEstimatedDeliveryDate() {
    await FHIRFetcherOnDemand(
      `/vendor/estimated-delivery/${props.vendor}?orgId=${props.patient!.org}`,
      authInfo.token,
    ).then(
      (data) => {
        setEstimatedDeliveryDateRange(moment(data.end).utc().toDate());
      },
      (_error) => {
        //return setEstimatedDeliveryDateRange(moment(data.end).utc().toDate());
      },
    );
  }

  function createEBTPendingOrder() {
    setOrdersMessage('Creating Order...');
    setWorking(true);
    setHideMessage(false);
    setWorkingOn('Creating');
    // add build order from FoodBundleItems
    const temp: Record<string, string | number | undefined | null> = {};

    const temp1 = orderDetails;
    if (Object.keys(temp1).length > 0) {
      for (const key of Object.keys(temp1)) {
        temp[key] = (temp1 as Record<string, string | number | undefined | null>)[key];
      }
    }

    const deliveryPreferences: string[] = [];

    // Same reasoning as the twin of this loop in `handleSubmit` above.
    props?.profileData?.entry?.forEach((entry: BundleEntry) => {
      const parsedObservations = FHIRUtils.parseFHIRPreference(entry.resource! as Observation);
      if (parsedObservations && parsedObservations.code === 'DELIVERY-PREFERENCES') {
        deliveryPreferences.push(parsedObservations.valueString);
      }
    });

    // Business wants delivery preferences the same order they created.
    temp['deliveryInstructions'] = deliveryPreferences.reverse().join(' | ');
    temp['estimatedDeliveryDate'] = moment(estimatedDeliveryDateRange).format('YYYY-MM-DD');
    setOrdersMessage('Creating order...');
    temp['lastUpdated'] = moment().utc().format();
    temp['lastUpdatedBy'] = authInfo.practitionerName;
    temp['orderDate'] = moment().utc().format();
    temp['createdDate'] = moment().utc().format();
    temp['createdBy'] = authInfo.practitionerName;
    //orderName is bunldeName in table. No bundleName or bundleId for orders created from table
    temp['patientId'] = props.patientId;
    temp['patientOrg'] = props.patient!.org;
    const docId = moment().unix().toString() + (Math.floor(Math.random() * 8999) + 1000);
    temp['orderId'] = docId;
    temp['docId'] = docId;
    temp['vendorName'] = props.vendorName;
    temp['vendorId'] = props.vendor;
    temp['status'] = 'EBT PENDING';

    setLoading(true);
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget write; a rejection is dropped and the user sees no failure
    FoodPoster<FoodOrderCreateResponse>(`/order/`, authInfo.token, temp).then((response) => {
      setWorking(true);
      setLoading(false);
      if (response.statusCode === '201' || response.statusCode === '200') {
        setOrdersMessage('Order Created Successfully!');
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; a rejection only leaves the view stale
        ordersMutate();
        setWorking(false);
      } else {
        setOrdersMessage('Error - ' + response.errorMessage);
        setWorking(false);
      }
    });
    setOrderDetails({});
    setWorking(false);
  }

  const handleSearchById = (e: unknown, searchProps: { value?: unknown }) => {
    setSearchId(searchProps.value as string);
  };

  const handleChangeDate = (event: unknown, { name: _name, value }: { name?: string; value: string }) => {
    setdateState(value);
    const temp = value.split(' - ');
    setStartRange(temp[0] as string);
    setEndRange(temp[1] as string);
    setStartExpectedDateRange('');
    setEndExpectedDateRange('');
    setExpectedDateState('');
    setDisableExpectedDueDateFilter(true);
  };

  //for estimated delivery date
  const handleChangeExpectedDate = (event: unknown, { name: _name, value }: { name?: string; value: string }) => {
    setExpectedDateState(value);
    const temp = value.split(' - ');
    setStartExpectedDateRange(temp[0] as string);
    setEndExpectedDateRange(temp[1] as string);
    setStartRange('');
    setEndRange('');
    setdateState('');
    setDisableCreatedDateFilter(true);
  };
  // `_.matches` shorthand, not a strict-equality predicate. They differ on an order carrying no
  // `orderId` property at all: `baseIsMatch` requires the key to be PRESENT (falling back to an
  // `in` test when the source value is `undefined`), while `o.orderId === undefined` reads a
  // missing key as `undefined` and matches. Measured against this repo's lodash 4.18.1 —
  // `[{}, {orderId:'B'}]` with an `undefined` needle gives upstream `undefined` and the predicate
  // form `{}`. That matters because `selectedOrder` is the sole gate on the Update Order modal,
  // so the predicate form can open it against a different member's order. The `as never` is a
  // type escape only; it erases.
  const selectedOrder =
    selectedOrdersLength === 1 &&
    ordersList &&
    _.filter(ordersList, { orderId: selectedOrderIds[0] } as never)[0];

  const updateContent = selectedOrdersLength === 1 && selectedOrder && (
    // We may need this restriction for vendor 'awaiting_shipment' orders in the future for MH
    // ((selectedOrder.vendorId === 'MH' && selectedOrder.vendorOrderStatus === 'awaiting_shipment')
    // ||
    //   selectedOrder.vendorId !== 'MH') &&
    <UpdateOrder
      vendor={props.vendor!}
      open={open}
      setOpen={setOpen}
      selectedOrder={selectedOrder && selectedOrder}
      authInfo={authInfo}
      setWorking={setWorking}
      setLoading={setLoading}
      setWorkingOn={setWorkingOn}
      ordersMutate={ordersMutate}
      invoiceStatusList={invoiceStatusList}
      ordersData={ordersData!}
      org={props.patient! && props.patient.org}
      ordersMealEquivalents={props.ordersMealEquivalents!}
      setOrdersMealEquivalents={props.setOrdersMealEquivalents!}
      setProgramOrdersCost={props.setProgramOrdersCost!}
      programOrdersCost={props.programOrdersCost!}
      programMAXMealEquivalents={props.programMAXMealEquivalents!}
      programMAXBudget={props.programMAXBudget!}
      setProgramMAXBudget={props.setProgramMAXBudget!}
      allReferralData={props.allReferralData!}
    />
  );

  let addContent: React.ReactNode = '';
  if (
    !props.foodCardOrdersOnly &&
    selectedOrdersLength === 0 &&
    props.patientId &&
    //ommmit catalog vendors to avoid ordering mistakes
    ![
      'PK',
      'SB',
      'MF',
      'All',
      'OK',
      'EW',
      'MH',
      'CFH',
      'FFH',
      VENDORS.NU,
      VENDORS.CJ,
      VENDORS.EF,
      VENDORS.MM,
      VENDORS.HH,
      VENDORS.CCF,
    ].includes(props.vendor!)
  ) {
    addContent = (
      <AddOrder
        open={open}
        org={props.patient!.org}
        setOpen={setOpen}
        flatPatient={flatPatient!}
        vendor={props.vendor!}
        vendorName={props.vendorName!}
        addOrder={addOrder as unknown as (event: React.FormEvent<HTMLFormElement>) => void}
        handleEstimateDeliveryDateChange={
          handleEstimateDeliveryDateChange as (event: unknown, estimatedDeliveryDate: { value?: unknown }) => void
        }
        handleChange={
          handleChange as (event: unknown, attributeName: string, value?: unknown, previous?: unknown) => void
        }
        orderDetails={orderDetails}
        estimatedDeliveryDateRange={estimatedDeliveryDateRange}
        estimatedDeliveryDateMutate={estimatedDeliveryDateMutate}
        ordersMealEquivalents={props.ordersMealEquivalents!}
        programMAXMealEquivalents={props.programMAXMealEquivalents!}
        setOrderDetails={setOrderDetails}
        loadingEstimatedDeliveryDate={loadingEDDRange}
        setProgramOrdersCost={props.setProgramOrdersCost!}
        programOrdersCost={props.programOrdersCost!}
        programMAXBudget={props.programMAXBudget!}
        setProgramMAXBudget={props.setProgramMAXBudget!}
        allReferralData={props.allReferralData!}
        referralId={referralId!}
        programBudget={props.programBudget!}
        setProgramBudget={props.setProgramBudget!}
      />
    );
  } else if (selectedOrdersLength === 0 && props.patientId && ['PK', 'MF'].includes(props.vendor!)) {
    addContent = (
      <Button
        content="EBT Pending Order"
        color="linkedin"
        icon="add"
        onClick={() => createEBTPendingOrder()}
      />
    );
  }

  const hasSelectedOrder = selectedOrdersLength === 1 && !!selectedOrderIds[0] && selectedOrder;

  const isLoading = loading || datasetLoading;

  return (
    <div style={{ minHeight: '65vh' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <Form autoComplete="off">
          <Form.Group>
            <Form.Input
              placeholder="Search by ID"
              icon={<Icon name="search" onClick={handleSearchById} />}
              value={searchId}
              onChange={(e, searchProps) => setSearchId(searchProps.value)}
              name="searchId"
            />
            <DatesRangeInput
              name="datesRange"
              placeholder="Filter by Date Range"
              value={dateState}
              iconPosition="left"
              dateFormat="YYYY-MM-DD"
              onChange={handleChangeDate}
              className={disableCreatedDateFilter ? 'disabled field' : ''}
            />
            <DatesRangeInput
              name="datesRange"
              placeholder="Filter by Exepcted Date Range"
              value={expectedDateState}
              iconPosition="left"
              dateFormat="YYYY-MM-DD"
              onChange={handleChangeExpectedDate}
              className={disableExpectedDueDateFilter ? 'disabled field' : ''}
            />
            <Form.Field
              control={Select}
              search
              name="status"
              selectOnBlur={false}
              scrolling
              button
              basic
              placeholder="Filter by Status"
              value={statusvalue}
              options={
                statusOptions &&
                statusOptions.map((x) => {
                  return { key: x, text: x!.toString().toUpperCase(), value: x };
                })
              }
              onChange={(e, statusprops: DropdownProps) => setStatusValue(statusprops.value as string)}
            />
            <Form.Button content="Clear" color="blue" onClick={resetFilters} />
          </Form.Group>
        </Form>
        <span style={{ float: 'right', paddingBottom: '20px' }}>
          {addContent}
          <span className="btn-group">
            {selectedOrdersLength === 1 && (
              <>
                {/*
                  SUSPECTED DEFECT — REPORTED, NOT FIXED. Same shape as the
                  "Details" button in `./table.tsx`: `openSolutranView` is optional
                  and only the Solutran-hosted renders pass it, but the button is
                  gated on `selectedOrdersLength === 1` alone. Selecting one order
                  anywhere else shows a "Pay" button whose click throws "not a
                  function" — the control is offered where the capability is not.
                */}
                <Button
                  icon="payment"
                  content="Pay"
                  onClick={() => props.openSolutranView!('makePayment', selectedOrderIds[0])}
                />
              </>
            )}
            {selectedOrdersLength === 1 && (
              <Button
                icon={<GiHealthNormal style={{ marginTop: '-3px', marginRight: '5px' }} />}
                content="Triage"
                color="red"
                onClick={() => setIsTriageModalOpen(true)}
              />
            )}
            {selectedOrdersLength === 1 && (
              <>
                {updateContent}
                {props.vendor === 'SB' && (
                  <>
                    <Confirm
                      open={sendConfirm}
                      onConfirm={sendOrders}
                      confirmButton={<Button content="Yes, Send Order" positive />}
                      cancelButton={<Button content="Never Mind" negative />}
                      onCancel={() => setSendConfirm(false)}
                    />
                    <Button
                      content="Send"
                      icon="send"
                      color="green"
                      onClick={() => setSendConfirm(true)}
                    />
                  </>
                )}
                <Confirm
                  open={cancelConfirm}
                  onConfirm={cancelOrders}
                  confirmButton={<Button content="Yes, Cancel Order" negative />}
                  cancelButton={<Button content="Do not Cancel" positive />}
                  onCancel={() => setCancelConfirm(false)}
                />
                <Button
                  icon="cancel"
                  content="Cancel"
                  color="red"
                  onClick={() => setCancelConfirm(true)}
                />
              </>
            )}
            {selectedOrdersLength > 0 && ordersList && !isLoading && !ordersError && (
              <ExportOrders
                vendor={props.vendor!}
                markExported={markExported}
                patients={patients}
                selectedOrderIds={selectedOrderIds}
                selectedOrdersLength={selectedOrdersLength}
                PATIENT_ATTRIBUTES={PATIENT_ATTRIBUTES}
                patientId={props.patientId!}
                ordersData={ordersList}
              />
            )}
          </span>
        </span>
      </div>
      <div>
        {isLoading && <Loader active inline="centered" content="Loading orders..." />}

        {ordersData && ordersList.length === 0 && !isLoading && (
          <Message warning header="Info" content="0 items found!" />
        )}
        {ordersError && !datasetLoading && (
          <Message header="Oops! Something went wrong..." content={ordersError.message} negative />
        )}
        {data && data.length > 0 && (
          <>
            {ordersMessage.length > 0 && (
              <>
                <Message
                  icon
                  negative={ordersMessage.toLowerCase().includes('error')}
                  positive={ordersMessage.toLowerCase().includes(' successful')}
                  hidden={hideMessage}
                >
                  {working && (
                    <>
                      <Icon name="circle notched" loading />
                      <Message.Content>
                        <Message.Header>Just one second</Message.Header>
                        {workingon}{' '}
                        {selectedOrderIds.length > 1
                          ? selectedOrderIds.length.toString() + ' orders'
                          : 'the order'}
                        ...
                      </Message.Content>
                    </>
                  )}
                  {!working && ordersMessage.length > 0 && (
                    <>
                      <Message.Content>
                        <Message.Header>
                          {ordersMessage.toLowerCase().includes(' successful')
                            ? workingon + ' Success'
                            : workingon + ' Error'}
                        </Message.Header>
                        {ordersMessage}
                      </Message.Content>
                      <Icon name="close" onClick={() => setHideMessage(true)} />
                    </>
                  )}
                </Message>
              </>
            )}
            {!isLoading && !ordersError && (
              <OrdersTable
                patientId={props.patientId!}
                org={props.patient! && props.patient.org}
                vendor={props.vendor!}
                data={data}
                patients={patients && patients}
                openSelectedPatient={openSelectedPatient}
                selectedOrderIds={selectedOrderIds}
                column={column!}
                direction={direction}
                addExportData={addExportData}
                selectAll={selectAll}
                dispatch={dispatch}
                openSolutranView={props.openSolutranView!}
                updateAlternateOption={updateAlternateOption}
                setEstimatedDeliveryDateRange={setEstimatedDeliveryDateRange}
                bundlesData={bundlesData!}
                bundlesLoading={bundlesLoading}
              />
            )}
            {isTriageModalOpen && hasSelectedOrder && (
              <FoodOrderTriageModalV2
                deliveryDate={selectedOrder?.deliveryDate || (selectedOrder?.estimatedDeliveryDate as string)}
                foodProvider={selectedOrder?.vendorId as string}
                onClose={() => setIsTriageModalOpen(false)}
                open={isTriageModalOpen}
                orderId={selectedOrderIds[0]!}
                ordersData={ordersData!}
                ordersMutate={ordersMutate as (data?: unknown, shouldRevalidate?: boolean) => Promise<unknown>}
                // SUSPECTED DEFECT — REPORTED, NOT FIXED. `FoodOrderTriageModalV2`
                // declares `orgId: string` and `patientId: string` as required
                // (foodOrderTriageModalV2/index.tsx:29-30), but an order from
                // `/food/order` carries both optionally and this render only
                // guards that *an* order is selected (`hasSelectedOrder`), not
                // that it has an org or a patient. The modal then puts both
                // straight into its fetch key, so an order missing either would
                // query a malformed path rather than fail loudly. The assertions
                // state the producer/consumer disagreement rather than resolve
                // it: adding `?? ''` here would send an empty org to the triage
                // API, and making the modal's fields optional is its contract to
                // change, not this file's.
                orgId={selectedOrder?.patientOrg as string}
                patientId={selectedOrder?.patientId as string}
                // `patients` is `Record<string, FlatPatient> | null` here, while
                // the modal asks for a narrower `{ firstName?, lastName? }` map.
                // `FlatPatient` types both `unknown[]` (fhirpath), so the two
                // genuinely disagree: the modal's template-string reads render an
                // array. Existing behaviour on that path.
                patients={
                  patients as unknown as Record<string, { firstName?: string; lastName?: string }>
                }
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
export default PatientOrders;
